import React, { useState, useMemo, useCallback } from 'react';
import {
  SVC_DB,
  RG_PRICE_VERSIONS,
  RG_CURRENT_VERSION,
  RG_LEGACY_VERSION,
  rgNormalizeVersion,
  rgCatalog,
  rgFindProduct,
  rgFindAddon,
  rgGetPrice,
  rgAddonPrice,
  rgAddonFits,
  rgPriceContext,
  rgBillableAddonIds,
  rgIsTiered,
} from '../../data/serviceCatalog';
import {
  VT_PRICE_VERSIONS,
  VT_CURRENT_VERSION,
  VT_LEGACY_VERSION,
  vtNormalizeVersion,
  vtExistsAt,
  vtPriceChanged,
  vtGetName,
  vtGetPrice,
  getVtAddonPrice,
  vtGetPackagePrice,
  findVtProduct,
  findVtPackage,
} from '../../data/virtourCatalog';
import { fmtC } from '../../lib/utils';

/* Width of the two VirTour price columns — shared by the cells and their header
   so the captions sit over the right columns in every row. Narrower on phones,
   where the columns otherwise crowd out the product name. */
const VT_COLS_W = 'w-[8.5rem] sm:w-[13rem]';
/* Rows are p-3 inside a 2px border, so their content starts 14px in. The header
   sits outside the rows and has to match that inset or every caption is drawn a
   column's-gap right of the cells it labels. */
const VT_COLS_INSET = 'pr-3.5';

/* Short tab labels */
const TAB_LABELS = {
  photo: 'Photo',
  drone: 'FPV',
  video: 'Video',
  floor: 'Floor',
  film: 'Film',
  aerial: 'Aerial/Drone',
  vt_photo: 'Photo',
  vt_tours: 'Tours & Plans',
  vt_film: 'Film',
  vt_social: 'Social',
  vt_web: 'Web',
  vt_staging: 'Staging',
};

/**
 * ServiceSelector — Tabbed category panel with inline add-ons.
 *
 * Drives either catalog: SVC_DB (Roto Flight's own RE services) or VIRTOUR_DB
 * (the mirrored VirTour order form, which adds packages and has no add-ons).
 *
 * VirTour ships two price sets, so every VirTour row offers two price cells —
 * old and current — and the one you click is the version that item is quoted
 * at. `selectedProducts[id]` therefore holds 'current' | 'legacy' rather than
 * `true`; both are truthy, so the RE path's selection checks are unaffected.
 *
 * RE ships two price lists too (Old, and New: the website's prices and 500 sq
 * ft bands), but chosen per QUOTE: a price-list tab above the categories
 * (`priceVersion` / `onSetPriceVersion`) swaps the whole catalog.
 */
export default function ServiceSelector({
  catalog = SVC_DB,
  priceVersion,
  selectedProducts = {},
  productQuantities = {},
  selectedAddons = {},
  addonQuantities = {},
  sqft,
  currentReTotal,
  onToggleProduct,
  onToggleAddon,
  onSetAddonQty,
  onSetSqft,
  onSetPriceVersion,
}) {
  const isVt = Array.isArray(catalog.packages);
  // RE sells from the quote's price list; VirTour from its own catalog.
  const reVersion = rgNormalizeVersion(priceVersion);
  const db = isVt ? catalog : rgCatalog(reVersion);
  const [activeTab, setActiveTab] = useState(db.categories[0]?.id);
  // The price list waiting on an "are you sure" (RE)
  const [pendingVersion, setPendingVersion] = useState(null);
  // { id, version } — the item waiting on a square footage before it is toggled
  const [pendingProduct, setPendingProduct] = useState(null);
  // { id, version } — the old-price pick waiting on an "are you sure"
  const [pendingLegacy, setPendingLegacy] = useState(null);
  const [sqftInput, setSqftInput] = useState('');
  const [openPackageId, setOpenPackageId] = useState(null);

  const sqftNum = parseInt(sqft) || 0;

  // RE: what the rest of the quote changes about a New-list price (the 2D Floor
  // Plan's shoot price, Aerial Photos beside ground photos, the reel's Timelapse
  // beside a Listing Video Timelapse that still bills). Built per price list,
  // since which add-ons bill depends on the list.
  const rePriceCtxAt = useCallback(
    (version) => rgPriceContext(
      Object.keys(selectedProducts),
      rgBillableAddonIds(selectedProducts, selectedAddons, sqftNum, version)
    ),
    [selectedProducts, selectedAddons, sqftNum]
  );
  const rePriceCtx = useMemo(() => rePriceCtxAt(reVersion), [rePriceCtxAt, reVersion]);
  const rePrice = (product, sf = sqftNum) => rgGetPrice(product, sf, reVersion, rePriceCtx);
  const reAddonPrice = (addon) => rgAddonPrice(addon, sqftNum, rePriceCtx);

  // Always read a selection through vtNormalizeVersion: a draft saved before
  // versioning holds `true`, which degrades to legacy — the safe direction,
  // since anything quoted then predates the reprice.
  const vtVersionOf = (id) =>
    selectedProducts[id] ? vtNormalizeVersion(selectedProducts[id]) : null;

  // RE mode has no versions; don't hand the reducer one.
  const emitToggle = (id, version) =>
    version ? onToggleProduct(id, version) : onToggleProduct(id);

  // VirTour: an add-on-priced product bills at its "+" rate when another item
  // is already on the order (mirrors the reprice in useQuoteBuilder).
  const vtRidesAlong = useCallback((id) =>
    Object.keys(selectedProducts).some((otherId) => {
      if (otherId === id) return false;
      if (findVtPackage(otherId)) return true;
      const other = findVtProduct(otherId);
      return !!other && other.product.addonPrice === undefined;
    }), [selectedProducts]);

  // `sqftOverride` lets the old-price confirmation quote the square footage the
  // user just typed without that figure having been written to the quote yet.
  const vtProductPrice = useCallback((product, version = VT_CURRENT_VERSION, sqftOverride) => {
    const sf = sqftOverride ?? sqftNum;
    return product.addonPrice !== undefined && vtRidesAlong(product.id)
      ? getVtAddonPrice(product, sf, version)
      : vtGetPrice(product, sf, version);
  }, [vtRidesAlong, sqftNum]);

  // Packages and products share the two-column UI, so both price through here.
  const vtIsPackage = (item) => Array.isArray(item.includes);

  const vtItemPrice = (item, version, sqftOverride) =>
    vtIsPackage(item)
      ? vtGetPackagePrice(item, sqftOverride ?? sqftNum, version).price
      : vtProductPrice(item, version, sqftOverride);

  // Tiered items quote "from" their smallest rung until a sq ft is known.
  const vtItemTiered = (item) => vtIsPackage(item) || item.flat === undefined;

  const vtCellText = (item, version, sqftOverride) => {
    const sf = sqftOverride ?? sqftNum;
    return (!sf && vtItemTiered(item) ? 'from ' : '') + fmtC(vtItemPrice(item, version, sf));
  };

  // Inside a price cell the "from" rides above the amount, so both columns keep
  // the same width whatever the price.
  const vtCellAmount = (item, version) => (
    <>
      {!sqftNum && vtItemTiered(item) && (
        <span className="block text-2xs font-normal opacity-70 leading-none">from</span>
      )}
      {fmtC(vtItemPrice(item, version))}
    </>
  );

  const vtNeedsSqft = (item) => !sqftNum && vtItemTiered(item);

  const vtFindItem = (id) => findVtPackage(id) || findVtProduct(id)?.product || null;

  // Add-ons for a category, filtered by excludeProducts and the New list's
  // size and requirement gates (same-day plans stop at 3,999 sq ft; captions
  // need the agent on camera)
  const getAddonsForCategory = (catId) =>
    (db.addons || []).filter((a) => {
      if (!a.applies.includes(catId)) return false;
      if (!rgAddonFits(a, sqftNum, selectedAddons)) return false;
      // If addon has excludeProducts, check that at least one selected product
      // in this category is NOT in the exclusion list
      if (a.excludeProducts?.length) {
        const cat = db.categories.find((c) => c.id === catId);
        if (cat) {
          const selectedInCat = cat.products.filter((p) => selectedProducts[p.id]);
          // Only exclude if ALL selected products in this category are excluded
          if (selectedInCat.length > 0 && selectedInCat.every((p) => a.excludeProducts.includes(p.id))) {
            return false;
          }
        }
      }
      return true;
    });

  // Price display
  const getPriceDisplay = (product) => {
    if (isVt) {
      if (product.flat !== undefined || sqftNum) return fmtC(vtProductPrice(product));
      return 'from ' + fmtC(product.tiers[0]);
    }
    // RE prices at the quote's list. Sized products quote "from" their
    // smallest size until a sq ft is known; null is a size the New list quotes.
    const sized = rgIsTiered(product) || !!product.filmBands;
    const { price } = rePrice(product);
    if (price == null) return 'Quote';
    return (sized && !sqftNum ? 'from ' : '') + fmtC(price);
  };

  // Duration display — VirTour's catalog carries no shoot durations
  const getDurDisplay = (product) => {
    if (isVt) return '';
    if (product.flat !== undefined) return `${rePrice(product).dur} min`;
    if (!sqftNum) return '\u2014';
    return `${rePrice(product).dur} min`;
  };

  // Count selected per category
  const countSelected = (cat) =>
    cat.products.filter((p) => selectedProducts[p.id]).length;

  /**
   * VirTour: a click on a price cell. Deselect (clicking the active cell) is
   * immediate; otherwise a missing sq ft is collected first and an old price is
   * confirmed last, so the confirmation shows the real tiered numbers. Only one
   * of the two modals is ever open.
   */
  const vtRequestToggle = (item, version) => {
    if (vtVersionOf(item.id) === version) {
      onToggleProduct(item.id, version);
      return;
    }
    if (vtNeedsSqft(item)) {
      setPendingLegacy(null);
      setPendingProduct({ id: item.id, version });
      setSqftInput('');
      return;
    }
    if (version === VT_LEGACY_VERSION) {
      setPendingLegacy({ id: item.id, version });
      return;
    }
    onToggleProduct(item.id, version);
  };

  // Sq ft modal "Continue" — hands an old-price pick on to its confirmation
  // instead of toggling it straight in. The square footage rides along unwritten
  // until that confirmation is answered: it reprices the WHOLE quote, so an
  // abandoned old-price click must not leave every other line on a tier the user
  // never chose (and lose their hand-typed rates to the reprice that follows).
  const commitSqft = () => {
    if (!sqftInput || !pendingProduct) return;
    const pend = pendingProduct;
    setPendingProduct(null);
    if (pend.version === VT_LEGACY_VERSION) {
      setPendingLegacy({ ...pend, sqft: sqftInput });
      return;
    }
    onSetSqft(sqftInput);
    emitToggle(pend.id, pend.version);
  };

  const confirmLegacy = () => {
    if (!pendingLegacy) return;
    if (pendingLegacy.sqft) onSetSqft(pendingLegacy.sqft);
    onToggleProduct(pendingLegacy.id, VT_LEGACY_VERSION);
    setPendingLegacy(null);
  };

  const vtPendingLegacyItem = pendingLegacy ? vtFindItem(pendingLegacy.id) : null;

  const vtVersionLabel = (id) => VT_PRICE_VERSIONS.find((v) => v.id === id)?.sub || '';

  /**
   * The two price cells. Red is reserved for an old price that genuinely
   * differs — where both sets agree there is nothing to warn about, and an item
   * added after the reprice has no old price to offer at all.
   */
  const vtPriceCells = (item) => {
    const active = vtVersionOf(item.id);
    const hasLegacy = vtExistsAt(item, VT_LEGACY_VERSION);
    const changed = vtPriceChanged(item, sqftNum);
    const base =
      'flex-1 min-w-0 px-1.5 sm:px-2 py-1 rounded border text-xs sm:text-sm font-semibold whitespace-nowrap text-center transition-colors';
    // Solid reds, not tints: this column is the one a reader must be able to
    // price off at a glance, and a translucent red on a light surface lands
    // around 2:1 against its own text.
    const legacyCls = changed
      ? (active === VT_LEGACY_VERSION
        ? 'bg-red-600 text-white border-red-600'
        : 'bg-red-50 text-red-700 border-red-300 hover:bg-red-100')
      : (active === VT_LEGACY_VERSION
        ? 'bg-accent text-bg border-accent'
        : 'bg-surface-2 text-text-muted border-border hover:bg-surface-2/60');
    const currentCls = active === VT_CURRENT_VERSION
      ? 'bg-accent text-bg border-accent'
      : 'bg-accent/10 text-accent border-accent/30 hover:bg-accent/20';

    const regPriceLine = (version) => {
      if (!vtIsPackage(item)) return null;
      const { price, regPrice } = vtGetPackagePrice(item, sqftNum, version);
      if (regPrice === undefined || regPrice === price) return null;
      return (
        <div className="text-2xs font-medium opacity-90 line-through leading-none">{fmtC(regPrice)}</div>
      );
    };

    return (
      <div className={`flex items-start gap-1.5 flex-shrink-0 ${VT_COLS_W}`}>
        {hasLegacy ? (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); vtRequestToggle(item, VT_LEGACY_VERSION); }}
            title={`Quote at VirTour's ${vtVersionLabel(VT_LEGACY_VERSION)} price`}
            className={`${base} ${legacyCls}`}
          >
            {vtCellAmount(item, VT_LEGACY_VERSION)}
            {regPriceLine(VT_LEGACY_VERSION)}
          </button>
        ) : (
          <span
            className={`${base} border-transparent text-text-dim font-normal`}
            title="Added after the reprice — no old price exists"
          >
            &mdash;
          </span>
        )}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); vtRequestToggle(item, VT_CURRENT_VERSION); }}
          title={`Quote at VirTour's ${vtVersionLabel(VT_CURRENT_VERSION)} price`}
          className={`${base} ${currentCls}`}
        >
          {vtCellAmount(item, VT_CURRENT_VERSION)}
          {regPriceLine(VT_CURRENT_VERSION)}
        </button>
      </div>
    );
  };

  /**
   * A VirTour product row. The name block is its own button rather than a
   * wrapper around the price cells — nesting buttons is invalid and the two
   * click targets would fight. Clicking it selects at the current price (or
   * clears the row); picking the old price is only ever done from its cell,
   * where the confirmation guards it.
   */
  const renderVtProductRow = (product) => {
    const active = vtVersionOf(product.id);
    const isSel = !!active;
    return (
      <div
        key={product.id}
        className={`w-full flex items-start gap-3 p-3 rounded-lg border-2 text-left transition ${
          isSel ? 'border-accent bg-accent/5' : 'border-border'
        }`}
      >
        <button
          type="button"
          onClick={() => vtRequestToggle(product, active || VT_CURRENT_VERSION)}
          className="flex-1 min-w-0 flex items-start gap-3 text-left"
        >
          <span
            className={`mt-0.5 w-5 h-5 flex-shrink-0 rounded border-2 flex items-center justify-center ${
              isSel ? 'bg-accent border-accent' : 'border-border'
            }`}
          >
            {isSel && (
              <svg className="w-3 h-3 text-bg" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            )}
          </span>

          {/* Name + desc + badges — badges sit here rather than beside the price,
              which leaves the two columns the room they need on a phone */}
          <span className="flex-1 min-w-0 block">
            <span className="block text-sm font-medium text-text-primary">
              {vtGetName(product, active || VT_CURRENT_VERSION)}
            </span>
            <span className="block text-xs text-text-muted mt-0.5">{product.desc}</span>
            {(product.note || product.perImage) && (
              <span className="flex flex-wrap gap-1.5 mt-1">
                {product.note && (
                  <span className="px-2 py-0.5 bg-accent/10 text-accent rounded text-xs font-medium">
                    {product.note}
                  </span>
                )}
                {product.perImage && (
                  <span className="px-2 py-0.5 bg-accent/10 text-accent rounded text-xs font-medium">
                    per image
                  </span>
                )}
              </span>
            )}
          </span>
        </button>

        {vtPriceCells(product)}
      </div>
    );
  };

  // Column captions, so the red cell reads as a choice and not an error.
  // `trailing` is a spacer standing in for whatever sits right of the cells.
  const vtColumnHeader = (trailing = null) => (
    <div className={`flex justify-end ${VT_COLS_INSET}`}>
      <div className={`flex items-center gap-1.5 text-2xs uppercase tracking-wider text-text-dim ${VT_COLS_W}`}>
        <span className="flex-1 min-w-0 text-center leading-tight">
          Old<span className="block normal-case opacity-80">{vtVersionLabel(VT_LEGACY_VERSION)}</span>
        </span>
        <span className="flex-1 min-w-0 text-center leading-tight">
          Current<span className="block normal-case opacity-80">{vtVersionLabel(VT_CURRENT_VERSION)}</span>
        </span>
      </div>
      {trailing}
    </div>
  );

  // RE totals at a price list — the summary strip at the quote's list, and the
  // switch confirmation at the other one. Add-ons count only where they bill
  // (rgBillableAddonIds, the rule the line rebuild uses). `dropped` names the
  // picks that list doesn't sell.
  const reTotalsAt = useCallback((version) => {
    let svcCount = 0;
    let aoCount = 0;
    let totalMin = 0;
    let totalPrice = 0;
    const dropped = [];
    const ctx = rePriceCtxAt(version);
    for (const pid of Object.keys(selectedProducts)) {
      const found = rgFindProduct(pid, version);
      if (!found) {
        dropped.push(rgFindProduct(pid, reVersion)?.product.name || pid);
        continue;
      }
      svcCount++;
      const { price, dur } = rgGetPrice(found.product, sqftNum, version, ctx);
      totalPrice += (price ?? 0) * (productQuantities[pid] || 1);
      totalMin += dur || 0;
    }
    for (const aid of rgBillableAddonIds(selectedProducts, selectedAddons, sqftNum, reVersion)) {
      if (!rgFindAddon(aid, version)) dropped.push(rgFindAddon(aid, reVersion)?.name || aid);
    }
    for (const aid of rgBillableAddonIds(selectedProducts, selectedAddons, sqftNum, version)) {
      const addon = rgFindAddon(aid, version);
      aoCount++;
      const qty = addon.perImage ? (addonQuantities[aid] || 1) : 1;
      totalPrice += (rgAddonPrice(addon, sqftNum, ctx) ?? 0) * qty;
      totalMin += addon.durAdd || 0;
    }
    return { svcCount, aoCount, totalMin, totalPrice, dropped };
  }, [selectedProducts, productQuantities, selectedAddons, addonQuantities, sqftNum, reVersion, rePriceCtxAt]);

  // Global summary
  const summary = useMemo(() => {
    if (!isVt) return reTotalsAt(reVersion);
    // VirTour has no add-ons and no shoot durations.
    let svcCount = 0;
    let totalPrice = 0;
    for (const pid of Object.keys(selectedProducts)) {
      // Each line bills at the version it was picked at, not at today's.
      const version = vtNormalizeVersion(selectedProducts[pid]);
      const pkg = findVtPackage(pid);
      if (pkg) {
        svcCount++;
        totalPrice += vtGetPackagePrice(pkg, sqftNum, version).price;
        continue;
      }
      const vtFound = findVtProduct(pid);
      if (!vtFound) continue;
      svcCount++;
      totalPrice += vtProductPrice(vtFound.product, version);
    }
    return { svcCount, aoCount: 0, totalMin: 0, totalPrice };
  }, [isVt, reTotalsAt, reVersion, selectedProducts, sqftNum, vtProductPrice]);

  // RE: moving the quote to the other price list reprices every RE line, so
  // with anything picked it asks first and shows what the move does.
  const requestVersion = (version) => {
    if (version === reVersion) return;
    if (Object.keys(selectedProducts).length === 0 && Object.keys(selectedAddons).length === 0) {
      onSetPriceVersion(version);
      return;
    }
    setPendingVersion(version);
  };

  const confirmVersion = () => {
    if (!pendingVersion) return;
    onSetPriceVersion(pendingVersion);
    setPendingVersion(null);
  };

  const rgVersionMeta = (id) => RG_PRICE_VERSIONS.find((v) => v.id === id) || {};

  const activeCat = db.categories.find((c) => c.id === activeTab);
  const activeAddons = activeCat ? getAddonsForCategory(activeCat.id) : [];
  const hasSel = activeCat ? countSelected(activeCat) > 0 : false;

  return (
    <div className="space-y-0">
      {/* ── Packages (VirTour only) ── */}
      {isVt && catalog.packages.length > 0 && (
        <div className="mb-3 space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-xs text-text-dim uppercase tracking-wider font-medium">
              Packages
            </span>
            <div className="h-px flex-1 bg-border" />
          </div>

          {vtColumnHeader(<span className="ml-3 w-28 flex-shrink-0" aria-hidden="true" />)}

          {catalog.packages.map((pkg) => {
            const active = vtVersionOf(pkg.id);
            const isSel = !!active;
            const isOpen = openPackageId === pkg.id;
            return (
              <div
                key={pkg.id}
                className={`rounded-lg border-2 transition ${
                  isSel ? 'border-accent bg-accent/5' : 'border-border'
                }`}
              >
                <div className="flex flex-wrap items-start gap-3 p-3">
                  <div className="flex-1 min-w-[10rem]">
                    {/* Two packages were renamed in the reprice — show the name
                        that belongs to the version this line is quoted at. */}
                    <div className="text-sm font-medium text-text-primary">
                      {vtGetName(pkg, active || VT_CURRENT_VERSION)}
                    </div>
                    <div className="text-xs text-text-muted mt-0.5">{pkg.desc}</div>
                    <button
                      type="button"
                      onClick={() => setOpenPackageId(isOpen ? null : pkg.id)}
                      className="mt-1.5 text-xs text-accent hover:underline"
                    >
                      {isOpen ? 'Hide includes' : `What's included (${pkg.includes.length})`}
                      {isOpen ? ' ▲' : ' ▼'}
                    </button>
                  </div>

                  <div className="flex items-center gap-3 flex-shrink-0 ml-auto">
                    {vtPriceCells(pkg)}
                    {/* The cells choose the version; this button is the plain
                        "current price" path and the way back off the quote. */}
                    <button
                      type="button"
                      onClick={() => vtRequestToggle(pkg, active || VT_CURRENT_VERSION)}
                      className={`w-28 px-3 py-1.5 rounded text-xs font-medium whitespace-nowrap transition-colors ${
                        isSel
                          ? 'bg-accent text-bg'
                          : 'bg-surface border border-accent/40 text-accent hover:bg-accent/10'
                      }`}
                    >
                      {isSel ? '✓ Added' : 'Add to quote'}
                    </button>
                  </div>
                </div>

                {isOpen && (
                  <ul className="px-3 pb-3 space-y-1">
                    {pkg.includes.map((item) => (
                      <li key={item} className="flex items-start gap-1.5 text-xs text-text-muted">
                        <span className="text-accent">&bull;</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Price list (RE) — Old on the left in red, as VirTour's columns ── */}
      {!isVt && onSetPriceVersion && (
        <div className="mb-3 flex items-center gap-2 flex-wrap">
          <span className="text-xs text-text-dim uppercase tracking-wider font-medium">
            Price list
          </span>
          {[RG_LEGACY_VERSION, RG_CURRENT_VERSION].map((id) => {
            const v = rgVersionMeta(id);
            const active = reVersion === id;
            const cls = id === RG_LEGACY_VERSION
              ? (active
                ? 'bg-red-600 text-white border-red-600'
                : 'bg-red-50 text-red-700 border-red-300 hover:bg-red-100')
              : (active
                ? 'bg-accent text-bg border-accent'
                : 'bg-accent/10 text-accent border-accent/30 hover:bg-accent/20');
            return (
              <button
                key={id}
                type="button"
                aria-pressed={active}
                onClick={() => requestVersion(id)}
                title={`Price this quote off the ${v.label} list (${v.sub})`}
                className={`px-3 py-1.5 rounded border text-sm font-semibold whitespace-nowrap transition-colors ${cls}`}
              >
                {v.label}
                <span className="ml-1.5 text-xs font-normal opacity-80">{v.sub}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* ── Tab Bar ── */}
      <div className="flex overflow-x-auto border-b border-border bg-surface rounded-t-lg">
        {db.categories.map((cat) => {
          const count = countSelected(cat);
          const isActive = cat.id === activeTab;
          return (
            <button
              key={cat.id}
              onClick={() => setActiveTab(cat.id)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                isActive
                  ? 'border-accent text-accent'
                  : 'border-transparent text-text-muted hover:text-text-primary hover:bg-surface-2/50'
              }`}
            >
              <span>{cat.icon}</span>
              <span>{cat.tab || TAB_LABELS[cat.id] || cat.name}</span>
              {count > 0 && (
                <span className="ml-1 px-1.5 py-0.5 bg-accent text-bg rounded-full text-2xs font-bold leading-none">
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* ── Active Category Panel ── */}
      {activeCat && (
        <div className="border border-t-0 border-border rounded-b-lg bg-surface p-4 space-y-4">
          {/* Category header */}
          <div>
            <h3 className="text-base font-semibold text-text-primary">{activeCat.name}</h3>
            <p className="text-sm text-text-muted mt-0.5">{activeCat.desc}</p>
          </div>

          {/* Products */}
          <div className="space-y-2">
            {isVt && vtColumnHeader()}
            {activeCat.products.map((product) => {
              if (isVt) return renderVtProductRow(product);
              const isSel = selectedProducts[product.id];
              return (
                <React.Fragment key={product.id}>
                {/* A heading where the New list groups a tab (listing cuts, reels) */}
                {product.group && (
                  <div className="flex items-center gap-2 pt-1">
                    <span className="text-xs text-text-dim uppercase tracking-wider font-medium">
                      {product.group}
                    </span>
                    <div className="h-px flex-1 bg-border" />
                  </div>
                )}
                <button
                  onClick={() => {
                    if (
                      !isSel &&
                      product.tiers &&
                      !product.flat &&
                      !product.filmBands &&
                      !sqftNum
                    ) {
                      setPendingProduct({ id: product.id });
                      setSqftInput('');
                      return;
                    }
                    onToggleProduct(product.id);
                  }}
                  className={`w-full flex items-start gap-3 p-3 rounded-lg border-2 text-left transition ${
                    isSel
                      ? 'border-accent bg-accent/5'
                      : 'border-border hover:border-border hover:bg-surface-2/50'
                  }`}
                >
                  {/* Checkbox */}
                  <span
                    className={`mt-0.5 w-5 h-5 flex-shrink-0 rounded border-2 flex items-center justify-center ${
                      isSel ? 'bg-accent border-accent' : 'border-border'
                    }`}
                  >
                    {isSel && (
                      <svg
                        className="w-3 h-3 text-bg"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </span>

                  {/* Name + desc */}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-text-primary">
                      {product.name}
                    </div>
                    <div className="text-xs text-text-muted mt-0.5">
                      {product.desc}
                    </div>
                  </div>

                  {/* Price + duration + badge */}
                  <div className="flex items-center gap-3 flex-shrink-0">
                    {product.note && (
                      <span className="px-2 py-0.5 bg-accent/10 text-accent rounded text-xs font-medium">
                        {product.note}
                      </span>
                    )}
                    {product.perImage && (
                      <span className="px-2 py-0.5 bg-accent/10 text-accent rounded text-xs font-medium">
                        per image
                      </span>
                    )}
                    {/* More than one, from a website order link (three Dynamic Reels) */}
                    {isSel && productQuantities[product.id] > 1 && (
                      <span className="px-2 py-0.5 bg-accent/10 text-accent rounded text-xs font-medium">
                        &times; {productQuantities[product.id]}
                      </span>
                    )}
                    <div className="text-right">
                      <div className="text-sm font-semibold text-accent">
                        {getPriceDisplay(product)}
                      </div>
                      {!isVt && (
                        <div className="text-xs text-text-dim">
                          {getDurDisplay(product)}
                        </div>
                      )}
                    </div>
                  </div>
                </button>
                </React.Fragment>
              );
            })}
          </div>



          {/* Add-ons section */}
          {hasSel && activeAddons.length > 0 && (
            <div className="pt-2">
              <div className="flex items-center gap-2 mb-3">
                <div className="h-px flex-1 bg-border" />
                <span className="text-xs text-text-dim uppercase tracking-wider font-medium">
                  Add-ons
                </span>
                <div className="h-px flex-1 bg-border" />
              </div>

              <div className="space-y-2">
                {activeAddons.map((addon) => {
                  const isChecked = selectedAddons[addon.id];
                  const qty = addonQuantities[addon.id] || 1;
                  // null: a size the New list quotes (the rate is typed on the line)
                  const aoPrice = reAddonPrice(addon);
                  return (
                    <div key={addon.id}>
                      <button
                        onClick={() => onToggleAddon(addon.id)}
                        className={`w-full flex items-start gap-3 p-3 rounded-lg border-2 text-left transition ${
                          isChecked
                            ? 'border-accent bg-accent/5'
                            : 'border-border hover:border-border hover:bg-surface-2/50'
                        }`}
                      >
                        {/* Checkbox */}
                        <span
                          className={`mt-0.5 w-5 h-5 flex-shrink-0 rounded border-2 flex items-center justify-center ${
                            isChecked ? 'bg-accent border-accent' : 'border-border'
                          }`}
                        >
                          {isChecked && (
                            <svg className="w-3 h-3 text-bg" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                        </span>

                        {/* Name + desc */}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-text-primary">
                            {addon.name}
                          </div>
                          <div className="text-xs text-text-muted mt-0.5">
                            {addon.desc}
                          </div>
                        </div>

                        {/* Price + badge */}
                        <div className="flex items-center gap-3 flex-shrink-0">
                          {addon.perImage && (
                            <span className="px-2 py-0.5 bg-accent/10 text-accent rounded text-xs font-medium">
                              {addon.unit || 'per image'}
                            </span>
                          )}
                          <div className="text-sm font-semibold text-accent">
                            {aoPrice == null ? 'Quote' : aoPrice === 0 ? 'Free' : fmtC(aoPrice)}
                          </div>
                        </div>
                      </button>

                      {/* Qty spinner for per-image */}
                      {isChecked && addon.perImage && (
                        <div className="flex items-center gap-2 mt-1.5 ml-8 text-sm text-text-muted">
                          <span>Qty:</span>
                          <input
                            type="number"
                            min="1"
                            max="50"
                            value={qty}
                            onChange={(e) => {
                              const v = Math.max(1, Math.min(50, parseInt(e.target.value) || 1));
                              onSetAddonQty(addon.id, v);
                            }}
                            className="w-14 px-2 py-1 bg-bg border border-border rounded text-sm text-text-primary"
                          />
                          <span>&times; {fmtC(aoPrice ?? 0)} = {fmtC((aoPrice ?? 0) * qty)}</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Summary Strip ── */}
      {summary.svcCount > 0 && (
        <div className="mt-3 px-4 py-2.5 bg-surface-2 border border-border rounded-lg flex items-center justify-between text-sm">
          <span className="text-text-muted">
            <span className="font-medium text-text-primary">{summary.svcCount}</span>
            {' service'}{summary.svcCount !== 1 ? 's' : ''}
            {summary.aoCount > 0 && (
              <>
                {' + '}
                <span className="font-medium text-text-primary">{summary.aoCount}</span>
                {' add-on'}{summary.aoCount !== 1 ? 's' : ''}
              </>
            )}
            {!isVt && (
              <>
                {' \u00b7 Est. '}
                <span className="font-medium text-text-primary">{summary.totalMin}</span>
                {' min'}
              </>
            )}
          </span>
          <span className="font-bold text-accent">{fmtC(summary.totalPrice)}</span>
        </div>
      )}

      {/* ── Sq Ft Popup ── */}
      {pendingProduct && (
        <div className="fixed inset-0 z-[1300] flex items-center justify-center bg-black/50">
          <div className="bg-surface border border-border rounded-xl shadow-xl w-80 p-5 space-y-4">
            <h3 className="text-sm font-heading text-text-primary uppercase">
              Enter Square Footage
            </h3>
            <p className="text-xs text-text-muted">
              This product&apos;s pricing is based on property size.
            </p>
            <input
              type="number"
              autoFocus
              value={sqftInput}
              onChange={(e) => setSqftInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitSqft();
              }}
              placeholder="e.g. 2500"
              className="w-full px-3 py-2 bg-bg border border-border rounded text-text-primary placeholder-text-muted focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setPendingProduct(null)}
                className="px-4 py-1.5 text-sm text-text-muted hover:text-text-primary"
              >
                Cancel
              </button>
              <button
                disabled={!sqftInput}
                onClick={commitSqft}
                className="px-4 py-1.5 text-sm bg-accent text-white rounded disabled:opacity-50"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Price-list switch confirmation (RE) ── */}
      {pendingVersion && (() => {
        const toOld = pendingVersion === RG_LEGACY_VERSION;
        const from = rgVersionMeta(reVersion);
        const to = rgVersionMeta(pendingVersion);
        // "Now" is what the RE lines add up to as they stand (rates typed by
        // hand included); the other side is the list they'd be repriced to.
        const now = currentReTotal ?? reTotalsAt(reVersion).totalPrice;
        const next = reTotalsAt(pendingVersion);
        const delta = next.totalPrice - now;
        return (
          // Same keyboard and backdrop handling as the VirTour confirmation below.
          <div
            role="dialog"
            aria-modal="true"
            aria-label={toOld ? 'Use the old prices?' : 'Use the new prices?'}
            onKeyDown={(e) => { if (e.key === 'Escape') setPendingVersion(null); }}
            onMouseDown={(e) => { if (e.target === e.currentTarget) setPendingVersion(null); }}
            className="fixed inset-0 z-[1300] flex items-center justify-center bg-black/50"
          >
            <div className="bg-surface border border-border rounded-xl shadow-xl w-80 p-5 space-y-4">
              <h3 className="text-sm font-heading text-text-primary uppercase">
                {toOld ? 'Use the old prices?' : 'Use the new prices?'}
              </h3>
              <p className="text-xs text-text-muted">
                {`Every Real Estate line on this quote would be repriced off the ${to.label} list (${to.sub}). `}
                {toOld && 'Only do this for a job agreed before the new prices. '}
                Rates typed by hand go back to the list price.
              </p>
              <div className="rounded border border-border bg-bg p-3 space-y-1 text-xs">
                <div className="flex justify-between gap-3">
                  <span className="text-text-muted">Now, {from.label} ({from.sub})</span>
                  <span className="text-text-primary">{fmtC(now)}</span>
                </div>
                <div className="flex justify-between gap-3">
                  <span className="text-text-muted">{to.label} ({to.sub})</span>
                  <span className={toOld ? 'text-red-700 font-semibold' : 'text-text-primary font-semibold'}>
                    {fmtC(next.totalPrice)}
                  </span>
                </div>
                <div className="flex justify-between gap-3 border-t border-border pt-1">
                  <span className="text-text-muted">Difference</span>
                  <span className={toOld ? 'text-red-700 font-semibold' : 'text-text-primary font-semibold'}>
                    {!delta ? 'None, same total' : `${delta > 0 ? '+' : '−'}${fmtC(Math.abs(delta))}`}
                  </span>
                </div>
              </div>
              {next.dropped.length > 0 && (
                <p className="text-xs text-text-muted">
                  {`Not on the ${to.label} list, so removed: `}
                  <span className="text-text-primary">{next.dropped.join(', ')}</span>
                </p>
              )}
              {!sqftNum && (
                <p className="text-2xs text-text-dim">No sq ft yet, so sized items are compared at their smallest size.</p>
              )}
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setPendingVersion(null)}
                  className="px-4 py-1.5 text-sm text-text-muted hover:text-text-primary"
                >
                  Cancel
                </button>
                <button
                  autoFocus
                  onClick={confirmVersion}
                  className={`px-4 py-1.5 text-sm text-white rounded ${
                    toOld ? 'bg-red-600 hover:bg-red-700' : 'bg-accent hover:opacity-90'
                  }`}
                >
                  {toOld ? 'Use old prices' : 'Use new prices'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Old-price confirmation (VirTour) ── */}
      {vtPendingLegacyItem && (
        // Escape and a backdrop click both cancel, and focus moves into the
        // dialog on open — otherwise it stays on the price cell behind the
        // overlay, where Enter would quietly add a different item while this
        // dialog is still asking about this one.
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Use the old price?"
          onKeyDown={(e) => { if (e.key === 'Escape') setPendingLegacy(null); }}
          onMouseDown={(e) => { if (e.target === e.currentTarget) setPendingLegacy(null); }}
          className="fixed inset-0 z-[1300] flex items-center justify-center bg-black/50"
        >
          <div className="bg-surface border border-border rounded-xl shadow-xl w-80 p-5 space-y-4">
            <h3 className="text-sm font-heading text-text-primary uppercase">
              Use the old price?
            </h3>
            <p className="text-xs text-text-muted">
              <span className="text-text-primary font-medium">
                {vtGetName(vtPendingLegacyItem, VT_LEGACY_VERSION)}
              </span>
              {` would be quoted off VirTour's ${vtVersionLabel(VT_LEGACY_VERSION)} sheet. `}
              Only do this for an order placed before the reprice.
            </p>
            <div className="rounded border border-border bg-bg p-3 space-y-1 text-xs">
              <div className="flex justify-between gap-3">
                <span className="text-text-muted">
                  Current ({vtVersionLabel(VT_CURRENT_VERSION)})
                </span>
                <span className="text-text-primary">
                  {vtCellText(vtPendingLegacyItem, VT_CURRENT_VERSION, pendingLegacy.sqft)}
                </span>
              </div>
              <div className="flex justify-between gap-3">
                <span className="text-text-muted">
                  Old ({vtVersionLabel(VT_LEGACY_VERSION)})
                </span>
                <span className="text-red-700 font-semibold">
                  {vtCellText(vtPendingLegacyItem, VT_LEGACY_VERSION, pendingLegacy.sqft)}
                </span>
              </div>
              <div className="flex justify-between gap-3 border-t border-border pt-1">
                <span className="text-text-muted">Difference</span>
                <span className="text-red-700 font-semibold">
                  {(() => {
                    const delta =
                      vtItemPrice(vtPendingLegacyItem, VT_LEGACY_VERSION, pendingLegacy.sqft)
                      - vtItemPrice(vtPendingLegacyItem, VT_CURRENT_VERSION, pendingLegacy.sqft);
                    if (!delta) return 'None — same price';
                    return `${delta > 0 ? '+' : '−'}${fmtC(Math.abs(delta))}`;
                  })()}
                </span>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setPendingLegacy(null)}
                className="px-4 py-1.5 text-sm text-text-muted hover:text-text-primary"
              >
                Cancel
              </button>
              <button
                autoFocus
                onClick={confirmLegacy}
                className="px-4 py-1.5 text-sm bg-red-600 text-white rounded hover:bg-red-700"
              >
                Use old price
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
